import React from 'react'

import { Button, Card, SectionHeading, Section } from '../../../../components/ui'
import { bookingPath, defaultWashSlug, enterBooking } from '../../../../lib/booking-entry'
import { useSelectors } from '../../../../store'
import { washCountLabel } from '../../../../lib/social-proof'

import './reviews.css'

// SLOT 7.5 - WHAT CUSTOMERS SAY. Objection it answers: "can I trust them?"
//
// Rewritten for conversion on 5 Oct 2026 (copy deck, Robin: "just do
// whatever is going to drive the most order conversions"). It leads on the
// wash count, puts the Google score second, links to the reviews on Google
// and ends on the booking. The three paragraphs explaining the size of the
// sample, the "start with the lowest ones" line and the private-survey coda
// are gone.
//
// HARD LIMITS THAT STILL HOLD:
//   - NO REVIEW COUNT, anywhere (Robin, 5 Oct). The count still arrives on
//     the gwmc:reviews meta tag and is used only as the COUNT_FLOOR guard
//     below; it is never rendered.
//   - The wash count is TOTAL_COMPLETED_WASHES from /api/v2/app_config
//     (`Order.where(completed: true, active: true).count`, cached), never
//     typed, with the same 1,000 floor and 26,000+ fallback as the fold.
//   - The score is `AppSecrets.google_rating` via MetaTags::Helper#google_reviews.
//   - No testimonial we wrote, and nothing from `feedbacks` (a private survey;
//     docs/AREA_PAGE_DATA.md). See QUOTES below.

// §2.4: show the score only at 4.5 or higher AND 50 or more reviews, and
// "wire the rating from config so this is a threshold, not a code change".
// The threshold lives here and the numbers come from the server, so the day
// the profile moves nobody has to find this file.
//
// RATING_FLOOR is 0 on purpose, which is a documented departure from §7.5
// ("no rating number until the 2.4 threshold is met") and the one place this
// section overrules the brief. A section whose argument is that it does not
// get to choose its own score cannot then decline to show it: the link is
// right there, the visitor is one tap from the number, and withholding it
// is the curation the copy has just disclaimed. The count floor stays, as a
// guard against stating a mean that too few people have earned.
const RATING_FLOOR = 0
const COUNT_FLOOR = 50

// Three real Google reviews, quoted verbatim, first name or initial only.
// EMPTY ON PURPOSE: the site stores no Google review text anywhere. The
// gwmc:reviews meta tag carries only the score, the count and the profile
// link (Google's API gives the mean and the count, not the reviews), and
// `feedbacks` notes are a private survey that may never be quoted. A quote
// may only be added from the public profile, word for word, never written,
// edited, merged or paraphrased. Shape: { text: '...', name: 'Sarah' }.
const QUOTES = []

// The profile's score and review count, read once and synchronously from
// the meta tag the layout writes. Same mechanism as the fold's prices and
// for the same reason: a number baked into the bundle goes stale and nobody
// notices, and an XHR for one line of text is not a trade worth making.
const readReviews = () => {
  const tag =
    typeof document === 'undefined'
      ? null
      : document.querySelector("meta[name='gwmc:reviews']")?.getAttribute('content')

  try {
    return tag ? JSON.parse(tag) : {}
  } catch (e) {
    return {}
  }
}

// The public profile. Hard-coded ONLY as the fallback for a page served
// before the meta tag lands, and it mirrors SeoHelper::GBP_URL exactly: the
// place id is a stable public identifier, not a figure that drifts. The
// number of reviews has no fallback on purpose.
const PROFILE_URL =
  'https://www.google.com/maps/search/?api=1&query=GoWashMyCar&query_place_id=ChIJx-etkF4FdkgRL3u0Hrbp8jQ'

const numberOf = value => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const Figure = ({ value, label }) => (
  <li className="reviews-figure">
    <span className="reviews-figure-value">{value}</span>
    <span className="reviews-figure-label">{label}</span>
  </li>
)

export const Reviews = () => {
  const { rating, count, url } = readReviews()
  const { config } = useSelectors()

  // SHOW_SOCIAL_PROOF off (lib/social-proof.js): no wash count, and the
  // gwmc:reviews tag carries no score, so the panel keeps only the link.
  const score = numberOf(rating)
  const reviewCount = numberOf(count)
  const washLabel = washCountLabel(config)

  // The count gates the score and is never shown.
  const showScore =
    score !== null && reviewCount !== null && score >= RATING_FLOOR && reviewCount >= COUNT_FLOOR

  return (
    <Section className="reviews">
      {/* Three children so the panel sits between the heading and the button
          on a phone and beside both of them at 1440. The grid places all
          three explicitly. */}
      <div className="reviews-grid">
        <SectionHeading
          className="reviews-head"
          intro={`${washLabel ? `${washLabel} cars` : 'Cars'} washed and valeted across every London borough since 2016. See what our customers say on Google.`}>
          London trusts us with its cars.
        </SectionHeading>

        <Card as="aside" className="reviews-proof">
          {washLabel && (
            <ul className="reviews-figures">
              <Figure value={washLabel} label="washes since 2016" />
            </ul>
          )}

          {showScore && (
            <p className="reviews-score">
              <span className="reviews-star" aria-hidden="true">
                ★
              </span>
              <span className="reviews-score-value">{score.toFixed(1)}</span>
              <span className="reviews-score-of">on Google</span>
            </p>
          )}

          <a
            className="gwmc-link reviews-link"
            href={url || PROFILE_URL}
            target="_blank"
            rel="noopener noreferrer">
            Read our Google reviews
            <span className="gwmc-sr-only"> (opens in a new tab)</span>
          </a>
        </Card>

        <div className="reviews-copy">
          <Button
            className="reviews-book"
            variant="primary"
            href={bookingPath(defaultWashSlug())}
            onClick={event => {
              event.preventDefault()
              enterBooking(defaultWashSlug(), 'reviews_section')
            }}>
            Book your wash
          </Button>
        </div>
      </div>

      {QUOTES.length > 0 && (
        <div className="gwmc-grid-3 reviews-quotes">
          {QUOTES.map(quote => (
            <figure className="reviews-quote" key={`${quote.name}-${quote.text.slice(0, 16)}`}>
              <blockquote className="reviews-quote-text">{quote.text}</blockquote>
              <figcaption className="reviews-quote-by">{quote.name}</figcaption>
            </figure>
          ))}
        </div>
      )}
    </Section>
  )
}
